import { useState, useRef, memo, useEffect } from 'react';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import MenuList from '@mui/material/MenuList';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import {
  Container,
  NotificationContainer,
  NotificationText,
  NotificationTitle,
  StyledNotificationIcon,
  StyledPaper,
  HeaderBox,
  HeaderTitle,
  NotificationBadgePill,
  IconWrapper,
  ContentBox,
  NotificationFooterTag,
  EmptyBox
} from './styles/NotificationStyle';
import { Badge, Typography, Button, IconButton, Tooltip, Box } from '@mui/material';
import useOnClickOutside from '../../utilities/hooks/useOnClickOutside';
import LocalStorageService from '../../services/LocalStorageService';
import { getNotifications, removeNotification } from '../../api/Notifications';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import COLORS from '../../theme/Colors';
import FormModal from '../FormModal/FormModal';

function Notifications({ color }) {
  const history = useHistory();
  const dispatch = useDispatch();

  const [menuOpen, setMenuOpen] = useState(false);
  const [citizenID, setCitizenID] = useState();
  const [detailNotification, setDetailNotification] = useState(null);
  const containerRef = useRef(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(['notificationsData', citizenID], () => getNotifications(citizenID), {
    enabled: typeof citizenID === 'string' ? true : false
  });
  const mutation = useMutation(removeNotification);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };

  useOnClickOutside(containerRef, () => setMenuOpen(false));

  const handleNotificationClick = async (item) => {
    mutation.mutate({
      citizenID,
      requestData: {
        notification: item.notification_id
      }
    }, {
      onSuccess: () => {
        queryClient.invalidateQueries('notificationsData');
      },
    });
    setMenuOpen(false);
    if (item["iD de solicitud"] !== undefined) {
      // La vista de detalles ya selecciona automáticamente la pestaña de
      // "Acción Requerida" o "Mensajes" según las acciones pendientes del
      // trámite, por lo que basta con enviar al usuario a la solicitud.
      history.push(`/app/serviceRequestedDetails/${item["iD de solicitud"]}`);
    } else {
      // Sin trámite asociado no hay a dónde navegar: mostramos el detalle
      // completo para que el clic no se sienta "muerto".
      setDetailNotification(item);
    }
  };

  const handleRemoveSingle = async (e, item) => {
    e.stopPropagation();
    try {
      await mutation.mutateAsync({
        citizenID,
        requestData: {
          notification: item.notification_id
        }
      });
    } catch (err) {
      console.error("Error al eliminar notificación:", err);
    } finally {
      queryClient.invalidateQueries('notificationsData');
    }
  };

  const handleClearAll = async (e) => {
    if (e) e.stopPropagation();
    const notifications = data?.notifications || [];
    if (!notifications || notifications.length === 0) return;
    try {
      await Promise.all(
        notifications.map(item =>
          mutation.mutateAsync({
            citizenID,
            requestData: {
              notification: item.notification_id
            }
          }).catch(() => null)
        )
      );
    } catch (err) {
      console.error("Error al limpiar notificaciones:", err);
    } finally {
      queryClient.invalidateQueries('notificationsData');
    }
  };

  useEffect(() => {
    setCitizenID(LocalStorageService.getItem('user_cedula'));
  }, [citizenID]);

  const notificationsList = data?.notifications || [];
  const unreadCount = notificationsList.length;

  return (
    <Container ref={containerRef}>
      <Badge
        badgeContent={unreadCount}
        color="secondary"
        overlap="circular"
        onClick={handleMenuOpen}
        sx={{
          '& .MuiBadge-badge': {
            bgcolor: COLORS.primary,
            color: '#FFFFFF',
            fontWeight: 800,
            fontSize: '0.72rem'
          }
        }}
      >
        <StyledNotificationIcon color={color} onClick={handleMenuOpen} />
      </Badge>

      <Popper
        open={menuOpen}
        anchorEl={containerRef.current}
        role={undefined}
        placement="bottom-end"
        transition
        disablePortal
        style={{ zIndex: 1200 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: 'right top' }}
          >
            <StyledPaper>
              {/* Header Box */}
              <HeaderBox sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <HeaderTitle>
                  <NotificationsOutlinedIcon sx={{ fontSize: 18, color: COLORS.primary }} />
                  Notificaciones
                  {unreadCount > 0 && (
                    <NotificationBadgePill>{unreadCount}</NotificationBadgePill>
                  )}
                </HeaderTitle>

                {unreadCount > 0 && (
                  <Button
                    size="small"
                    onClick={handleClearAll}
                    sx={{
                      fontSize: '11.5px',
                      fontWeight: 600,
                      textTransform: 'none',
                      color: '#64748B',
                      p: 0,
                      minWidth: 'auto',
                      '&:hover': { color: COLORS.primary, bgcolor: 'transparent', textDecoration: 'underline' }
                    }}
                  >
                    Marcar todas leídas
                  </Button>
                )}
              </HeaderBox>

              {/* Scrollable Notifications List */}
              <MenuList
                sx={{
                  maxHeight: '360px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: 0,
                  '&::-webkit-scrollbar': {
                    width: '5px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#E2E8F0',
                    borderRadius: '10px',
                  }
                }}
              >
                {unreadCount > 0 ? (
                  notificationsList.map((item, index) => {
                    const isApproved = String(item.Evento || "").toLowerCase().includes("aprobad") || String(item.Evento || "").toLowerCase().includes("completad");
                    const iconToShow = isApproved ? (
                      <CheckCircleOutlinedIcon sx={{ fontSize: 18, color: '#10B981' }} />
                    ) : (
                      <AssignmentOutlinedIcon sx={{ fontSize: 18, color: COLORS.primary }} />
                    );

                    return (
                      <NotificationContainer
                        key={index}
                        onClick={() => handleNotificationClick(item)}
                        isRecent={true}
                        sx={{
                          position: 'relative',
                          '& .action-btns': { opacity: 0, transition: 'opacity 0.2s' },
                          '&:hover .action-btns': { opacity: 1 }
                        }}
                      >
                        <IconWrapper>
                          {iconToShow}
                        </IconWrapper>
                        <ContentBox>
                          <NotificationTitle>
                            {item.Evento || "Actualización de Solicitud"}
                          </NotificationTitle>
                          <NotificationText>
                            {item.title || item.mensaje || "Tienes un evento pendiente en tu trámite."}
                          </NotificationText>
                          {item["iD de solicitud"] && (
                            <NotificationFooterTag>
                              Trámite #{item["iD de solicitud"]}
                              <ArrowForwardIcon sx={{ fontSize: 11 }} />
                            </NotificationFooterTag>
                          )}
                        </ContentBox>

                        {/* Discrete Hover Actions */}
                        <Box className="action-btns" sx={{ display: 'flex', alignItems: 'center', gap: 0.2, ml: 1 }}>
                          <Tooltip title="Eliminar / Marcar leída">
                            <IconButton
                              size="small"
                              onClick={(e) => handleRemoveSingle(e, item)}
                              sx={{
                                color: '#94A3B8',
                                p: 0.4,
                                '&:hover': { color: '#EF4444', bgcolor: 'rgba(239, 68, 68, 0.08)' }
                              }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </NotificationContainer>
                    );
                  })
                ) : (
                  <EmptyBox>
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: '50%',
                        backgroundColor: '#F1F5F9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 10
                      }}
                    >
                      <NotificationsOutlinedIcon sx={{ fontSize: 22, color: '#94A3B8' }} />
                    </div>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A', mb: 0.3, fontSize: '13px' }}>
                      Sin notificaciones
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B', maxWidth: 220, lineHeight: 1.3, fontSize: '11.5px' }}>
                      Te avisaremos aquí cuando haya novedades sobre tus solicitudes.
                    </Typography>
                  </EmptyBox>
                )}
              </MenuList>

              {/* Footer Callout Link */}
              {citizenID && (
                <Box
                  sx={{
                    px: 2,
                    py: 1.2,
                    backgroundColor: '#F8FAFC',
                    borderTop: '1px solid #F1F5F9',
                    textAlign: 'center'
                  }}
                >
                  <Button
                    size="small"
                    variant="text"
                    sx={{
                      color: COLORS.primary,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      p: 0,
                      '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                    }}
                    onClick={() => {
                      setMenuOpen(false);
                      history.push('/app/mis-solicitudes');
                    }}
                  >
                    Ver mi escritorio de solicitudes <ArrowForwardIcon sx={{ fontSize: 13, ml: 0.5 }} />
                  </Button>
                </Box>
              )}
            </StyledPaper>
          </Grow>
        )}
      </Popper>

      <FormModal
        open={Boolean(detailNotification)}
        onClose={() => setDetailNotification(null)}
        title={detailNotification?.Evento || 'Actualización de Solicitud'}
        maxWidth="sm"
      >
        <Box sx={{ px: 1, pb: 2 }}>
          <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
            {detailNotification?.title || detailNotification?.mensaje || 'No hay más detalles disponibles para esta notificación.'}
          </Typography>
        </Box>
      </FormModal>
    </Container>
  );
}

export default memo(Notifications);